define('presentation/settings/reports/viewModels/editReportViewModel',[
    'businessServices/router/router',
    'businessServices/state/modelStateObserver',
    'common/collections/collectionSorter',
    'common/promises/promiseFactory',
    'constants/scheduledReportDeliveryFrequencyConstants',
    'constants/scheduledReportDeliveryFormatConstants',
    'constants/scheduledReportGraphByConstants',
    'constants/timeframeConstants',
    'presentation/settings/reports/facades/editReportFacade',
    'presentation/settings/reports/validators/editReportViewModelValidator',
    'settings/navigationConfiguration'
], function () {
    return function () {
        const self = this;

        const SorterConstructor = require('common/collections/collectionSorter');
        const _sorter = new SorterConstructor();

        const ModelStateObserverConstructor = require('businessServices/state/modelStateObserver');

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const _commonState = require('common/storage/commonState');
        const _i18n = require('i18next');
        const _navigationConfiguration = require('settings/navigationConfiguration');
        const _router = require('businessServices/router/router');
        const _scheduledReportDeliveryFrequencyConstants = require('constants/scheduledReportDeliveryFrequencyConstants');
        const _scheduledReportDeliveryFormatConstants = require('constants/scheduledReportDeliveryFormatConstants');
        const _scheduledReportGraphByConstants = require('constants/scheduledReportGraphByConstants');
        const _timeframeConstants = require('constants/timeframeConstants');

        let _disposables = [];
        let _facade = null;
        let _validator = null;

        const MAX_ITEMS_CAN_ADD = 10;

        self.modelStateObserver = null;
        self.isCompositionComplete = ko.observable(false);
        self.showRequiredItemsWarning = ko.observable(false);

        self.scheduledReportId = null;
        self.reportsUrl = _navigationConfiguration.routesById.reports.url;

        self.subject = ko.observable("").extend({observeState: true});
        self.message = ko.observable("").extend({observeState: true});
        self.sendToEmailAddresses = ko.observableArray([]).extend({observeState: true});

        self.selectedDeliveryFormat = ko.observable(_scheduledReportDeliveryFormatConstants.Csv).extend({observeState: true});
        self.deliveryFormatOptions = [
            {id: _scheduledReportDeliveryFormatConstants.Csv, text: _i18n.t('editReport:csv')},
            {id: _scheduledReportDeliveryFormatConstants.Pdf, text: _i18n.t('editReport:pdf')}
        ];

        self.showGraphBy = ko.pureComputed(() => self.selectedDeliveryFormat() === _scheduledReportDeliveryFormatConstants.Pdf);
        self.selectedGraphBy = ko.observable(_scheduledReportGraphByConstants.Week).extend({observeState: true});
        self.graphByOptions = [
            {id: _scheduledReportGraphByConstants.Hour, text: _i18n.t('editReport:hour')},
            {id: _scheduledReportGraphByConstants.Day, text: _i18n.t('editReport:day')},
            {id: _scheduledReportGraphByConstants.Week, text: _i18n.t('editReport:week')},
            {id: _scheduledReportGraphByConstants.Month, text: _i18n.t('editReport:month')}
        ];

        self.selectedDeliveryFrequency = ko.observable(_scheduledReportDeliveryFrequencyConstants.Daily).extend({observeState: true});
        self.deliveryFrequencyOptions = [
            {id: _scheduledReportDeliveryFrequencyConstants.Daily, text: _i18n.t('editReport:daily')},
            {id: _scheduledReportDeliveryFrequencyConstants.Weekly, text: _i18n.t('editReport:weekly')},
            {id: _scheduledReportDeliveryFrequencyConstants.Monthly, text: _i18n.t('editReport:monthly')}
        ];

        self.timeframe = ko.observable(null);
        self.timeframeText = ko.pureComputed(() => {
            switch (self.timeframe()) {
                case _timeframeConstants.today:
                    return _i18n.t('timeframe:today');
                case _timeframeConstants.thisWeek:
                    return _i18n.t('timeframe:thisWeek');
                case _timeframeConstants.thisMonth:
                    return _i18n.t('timeframe:thisMonth');
                case _timeframeConstants.thisQuarter:
                    return _i18n.t('timeframe:thisQuarter');
                case _timeframeConstants.thisYear:
                    return _i18n.t('timeframe:thisYear');
                case _timeframeConstants.yesterday:
                    return _i18n.t('timeframe:yesterday');
                case _timeframeConstants.lastWeek:
                    return _i18n.t('timeframe:lastWeek');
                case _timeframeConstants.lastMonth:
                    return _i18n.t('timeframe:lastMonth');
                case _timeframeConstants.lastQuarter:
                    return _i18n.t('timeframe:lastQuarter');
                case _timeframeConstants.lastYear:
                    return _i18n.t('timeframe:lastYear');
                case _timeframeConstants.monthToDate:
                    return _i18n.t('timeframe:monthToDate');
                case _timeframeConstants.quarterToDate:
                    return _i18n.t('timeframe:quarterToDate');
                case _timeframeConstants.yearToDate:
                    return _i18n.t('timeframe:yearToDate');
                case _timeframeConstants.lastDays7:
                    return _i18n.t('timeframe:lastDays7');
                case _timeframeConstants.lastDays14:
                    return _i18n.t('timeframe:lastDays14');
                case _timeframeConstants.lastDays30:
                    return _i18n.t('timeframe:lastDays30');
                case _timeframeConstants.lastDays60:
                    return _i18n.t('timeframe:lastDays60');
                case _timeframeConstants.lastDays90:
                    return _i18n.t('timeframe:lastDays90');
                case _timeframeConstants.lastMonths12:
                    return _i18n.t('timeframe:lastMonths12');
                case _timeframeConstants.custom:
                    return _i18n.t('timeframe:custom');
                default:
                    return '';
            }
        });

        self.involvingHostedNumbers = ko.observableArray([]);
        self.involvingHostedNumbersText = ko.pureComputed(() => {
            if (self.involvingHostedNumbers().length === 0) {
                return _i18n.t('editReport:all');
            }
            return _i18n.t('editReport:involvingHostedNumbersText', { count: self.involvingHostedNumbers().length });
        });
        self.showHostedNumbers = ko.observable(false).toggleable();
        self.displayHostedNumbers = ko.pureComputed(() => {
            const formattedHostedNumbers =  self.involvingHostedNumbers().map(id => {
                const commonStateHostedNumber = _commonState.get(id);
                return {
                    id: commonStateHostedNumber.id,
                    displayName: commonStateHostedNumber.name
                };
            });
            _sorter.sort(formattedHostedNumbers, "displayName", true);
            return formattedHostedNumbers;
        });
        self.canToggleHostedNumbers = ko.pureComputed(() => self.displayHostedNumbers().length !== 0);

        self.involvingUsers = ko.observableArray([]);
        self.involvingUserGroups = ko.observableArray([]);
        self.involvingUsersAndGroupsText = ko.pureComputed(() => {
            if (self.involvingUsers().length === 0 && self.involvingUserGroups().length === 0) {
                return _i18n.t('editReport:all');
            }
            return _i18n.t('editReport:involvingUsersAndGroupsText', { count: self.involvingUsers().length + self.involvingUserGroups().length });
        });
        self.showUsersAndGroups = ko.observable(false).toggleable();
        self.displayUsersAndGroups = ko.pureComputed(() => {
            const usersAndGroups = self.involvingUsers().concat(self.involvingUserGroups());
            const formattedUsersAndGroups =  usersAndGroups.map(id => {
                const commonStateUserOrGroup = _commonState.get(id);                
                return {
                    id: commonStateUserOrGroup.id,
                    displayName: commonStateUserOrGroup.name,
                    avatar: commonStateUserOrGroup.type === _commonState.types.user ? commonStateUserOrGroup.avatar : null,
                    isUser: commonStateUserOrGroup.type === _commonState.types.user,
                    isUserGroup: commonStateUserOrGroup.type === _commonState.types.userGroup
                };
            });
            _sorter.sort(formattedUsersAndGroups, "displayName", true);
            return formattedUsersAndGroups;
        });
        self.canToggleUsersAndGroups = ko.pureComputed(() => self.displayUsersAndGroups().length !== 0);

        self.canAddAnotherEmailAddress = ko.pureComputed(() => self.sendToEmailAddresses().length < MAX_ITEMS_CAN_ADD);
        self.canDeleteEmailAddress = ko.pureComputed(() => self.sendToEmailAddresses().length > 0);
        self.addEmailAddressBtnText = ko.pureComputed(() => {
            if (self.canAddAnotherEmailAddress()) {
                return _i18n.t('editReport:addEmailAddress');
            } else {
                return _i18n.t('editReport:disabledAddEmailAddress');
            }
        });

        const _addDefaultEmailAddress = () => {
            const email = {
                index: null,
                emailAddress: ko.observable('').extend({ rateLimit: 500 })
            };
            self.sendToEmailAddresses.push(email);
            _reIndexEmailAddresses();
        };

        const _extractEmailAddresses = (emailAddresses) => {
            return emailAddresses.map(email => email.emailAddress());
        };

        const _reIndexEmailAddresses = () => {
            self.sendToEmailAddresses().forEach((email, index) => email.index = index);
        };

        const _removeDuplicateFieldsBeforeValidation = () => {
            const duplicateEmailAddresses = self.sendToEmailAddresses().filter((email, index) => {
                return self.sendToEmailAddresses().find((e, i) => {
                    return e.emailAddress().trim() === email.emailAddress().trim() &&
                        index !== 0 &&
                        index !== i;
                });
            });
            self.sendToEmailAddresses.removeAll(duplicateEmailAddresses);
        };

        const _removeEmptyFieldsBeforeSave = () => {
            const emptyAddedEmailAddresses = self.sendToEmailAddresses().filter((email) => {
                return email.emailAddress().trim() === "";
            });
            self.sendToEmailAddresses.removeAll(emptyAddedEmailAddresses);
        };

        const _populateScheduledReportForm = (scheduledReport) => {
            const {
                subject,
                message,
                deliveryFrequency,
                graphDataPointsBy,
                deliveryFormat,
                sendToEmailAddresses,
                timeframe,
                involvingHostedNumbers,
                involvingUsers,
                involvingUserGroups
            } = scheduledReport;

            self.subject(subject);
            if (message) {
                self.message(message);
            }
            self.selectedDeliveryFrequency(ko.unwrap(deliveryFrequency));
            self.selectedGraphBy(ko.unwrap(graphDataPointsBy));
            self.selectedDeliveryFormat(ko.unwrap(deliveryFormat));
            if (timeframe) {
                self.timeframe(timeframe);
            }
            if (involvingHostedNumbers) {
                self.involvingHostedNumbers(involvingHostedNumbers);
            }
            if (involvingUsers) {
                self.involvingUsers(involvingUsers);
            }
            if (involvingUserGroups) {
                self.involvingUserGroups(involvingUserGroups);
            }
            if (sendToEmailAddresses.length === 0) {
                _addDefaultEmailAddress();
            } else {
                sendToEmailAddresses.map((emailAddress, index) => {
                    const recipient = {
                        index: index,
                        emailAddress: ko.observable('').extend({ rateLimit: 500 })
                    };
                    recipient.emailAddress(emailAddress);
                    self.sendToEmailAddresses.push(recipient);
                });
            }            
        };

        const _saveScheduledReport = () => {
            return _promiseFactory.defer((promise) => {
                _removeDuplicateFieldsBeforeValidation();
                self.validate()
                    .fail(promise.reject)
                    .done((isValid) => {
                        if (isValid === false) {
                            promise.resolve();
                            return;
                        }

                        _removeEmptyFieldsBeforeSave();

                        _facade.updateScheduledReport(
                            self.scheduledReportId,
                            self.subject().trim(),
                            self.message().trim(),
                            self.selectedDeliveryFormat(),
                            self.selectedGraphBy(),
                            self.selectedDeliveryFrequency(),
                            _extractEmailAddresses(self.sendToEmailAddresses())
                        )
                            .fail(promise.reject)
                            .done((result) => {
                                if (result.isSuccessful) {
                                    promise.resolve();
                                } else {
                                    promise.reject();
                                }
                            });
                    });
            });
        };

        self.onRemoveEmailAddressClick = (emailToDelete) => {
            if (self.canDeleteEmailAddress() === false) {
                return;
            }
            self.sendToEmailAddresses.remove(emailToDelete);
        };

        self.onAddEmailAddressClick = () => {
            if (self.canAddAnotherEmailAddress() === false) {
                return;
            }
            _addDefaultEmailAddress();
        };

        self.toggleHostedNumbers = () => {
            self.showHostedNumbers.toggle();
        };

        self.toggleUsersAndGroups = () => {
           self.showUsersAndGroups.toggle();
        };

        self.shouldScrollTop = ko.observable(false);

        self.validate = () => {
            return _promiseFactory.defer((deferredObject) => {
                _validator.validateMinimumRequiredFields()
                    .fail(deferredObject.reject)
                    .done((hasAtLeastOneRequiredField) => {
                        self.showRequiredItemsWarning(!hasAtLeastOneRequiredField);
                        _validator.validate()
                            .fail(deferredObject.reject)
                            .done((isValid) => {
                                deferredObject.resolve(hasAtLeastOneRequiredField && isValid);
                            });
                    });
            });
        };
        self.save = () => {
            return _promiseFactory.defer((promise) => {
                _saveScheduledReport()
                    .fail(promise.reject)
                    .done(() => {
                        self.modelStateObserver.saveData();
                        _router.navigate(self.reportsUrl);
                        promise.resolve(true);
                    });
            });
        };

        self.cancel = () => {
            self.modelStateObserver.navigateWithoutWarning = true;
            _router.navigate(self.reportsUrl);
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
        };

        self.detached = () => {
            _disposables.forEach(d => d.dispose());
            _disposables = [];
        };

        self.activate = (reportId) => {
            if (reportId) {
                self.scheduledReportId = reportId;
            }

            const FacadeConstructor = require('presentation/settings/reports/facades/editReportFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const ValidatorConstructor = require('presentation/settings/reports/validators/editReportViewModelValidator');
            _validator = new ValidatorConstructor();

            return _initialize();
        };

        const _initialize = () => {
            _promiseFactory.defer((promise) => {

                self.modelStateObserver = new ModelStateObserverConstructor(self, true);

                _facade.getScheduledReport(self.scheduledReportId)
                    .fail(promise.reject)
                    .done((scheduledReport) => {
                        _populateScheduledReportForm(scheduledReport);
                        self.modelStateObserver.commitData();
                        _validator.registerViewModel(self);
                        promise.resolve();
                    });

            });

            return _promiseFactory.wait();
        };
    };
});
